import React from "react";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";

import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import PropTypes from "prop-types";

import styles from "assets/jss/material-dashboard-pro-react/views/userProfileStyles.js";

const useStylesBase = makeStyles(styles);

const stylesCenter = {
  center: {
    textAlign: "center",
  },
};

const stylesSize = {
  size: {
    fontSize: "large",
    color: "black",
    opacity: 1,
    textAlign: "left",
  },
};

const styleHeading = {
  heading: {
    fontWeight: "bold",
    textAlign: "center",
  },
};

const stylesList = {
  list: {
    padding: 0,
    textAlign: "center",
    listStyle: "inside",
    fontSize: "large",
  },
};

const useStylesCenter = makeStyles(stylesCenter);
const useStylesSize = makeStyles(stylesSize);
const useStylesList = makeStyles(stylesList);
const useStylesHeading = makeStyles(styleHeading);

export default function Dino(props) {
  const classesBase = useStylesBase();
  const classesList = useStylesList();
  const classesSize = useStylesSize();
  const classesCenter = useStylesCenter();
  const classesHeading = useStylesHeading();

  const classes = {
    ...classesBase,
    ...classesSize,
    ...classesList,
    ...classesCenter,
    ...classesHeading,
  };

  const access = props.userInfo && props.userInfo.access;

  const minimumAccessNeeded = 3.5; // for research coordinator access

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card profile>
          <CardBody profile>
            <div className={classes.size}>
              <h3 className={classes.heading}>
                Dead Space and Inhaled Nitric Oxide in Pediatric Acute
                Respiratory Distress Syndrome (The DiNO Study)
              </h3>
              <div>
                <br></br>
                <img
                  src="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+pic.jpg"
                  alt="Dino Image"
                  style={{
                    width: "25%",
                    maxWidth: "400px",
                    display: "block",
                    margin: "0 auto",
                  }}
                />
                <h4 className={classes.heading}>Study Overview</h4>

                <h5 className={classes.center}>
                  Children with acute respiratory distress syndrome (ARDS), a
                  form of severe lung injury, have a high risk of death (20%)
                  and there are no known beneficial medications to lower this
                  risk. We will test whether measuring dead space, a marker of
                  abnormal lung function, may improve our ability to identify
                  which children are at highest risk of dying from ARDS and the
                  children who may benefit from a medication to improve lung
                  function (inhaled Nitric Oxide). The results of this research
                  could have a major impact on designing clinical trials to
                  identify therapies that may decrease the risk of mortality in
                  ARDS. We plan to enroll 1,260 children over 4 years with
                  moderate to severe pediatric ARDS across multiple clinical
                  sites in the DiNO Study. In children with severe pediatric
                  ARDS, who are at the highest risk of death and are most likely
                  to receive iNO, we will collect longitudinal blood samples to
                  assess blood markers of problems with blood vessels or
                  clotting.
                </h5>
                <br></br>

                <h4 className={classes.heading}>Abstract</h4>

                <ul className={classes.list}>
                  <li>
                    <a
                      href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+Abstract.docx"
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      Download Abstract
                    </a>
                  </li>
                </ul>

                <br></br>

                {access >= minimumAccessNeeded && (
                  <div>
                    <h4 className={classes.heading}>CRFs</h4>

                    <ul className={classes.list}>
                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+Baseline+CRF.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Baseline CRF w Appendix
                        </a>
                      </li>
                      <br></br>
                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+DailyData+CRF.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Daily Data CRF
                        </a>
                      </li>
                      <br></br>
                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+Outcome+CRF.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Outcomes CRF
                        </a>
                      </li>
                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+Summary+Med+CRF.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Summary Medications Therapies CRF
                        </a>
                      </li>
                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+Sample+Collection+CRF.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Sample Collection CRF
                        </a>
                      </li>
                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+AE.PV+Log.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          AE/PV form
                        </a>
                      </li>
                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+SAE+Reporting+Form.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          SAE form
                        </a>
                      </li>

                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/Supply+Request+Form.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Aim 3 Supply Request Form
                        </a>
                      </li>
                    </ul>

                    <br></br>

                    <h4 className={classes.heading}>Job Aids / SOPs</h4>

                    <ul className={classes.list}>
                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/SOP_Sample+Processing.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Sample Processing SOP
                        </a>
                      </li>
                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/Website+Job+Aid.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          DiNO website Job Aid
                        </a>
                      </li>
                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/Reference_DiNO+Queries+Job+Aid.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Queries Job Aid
                        </a>
                      </li>
                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/Deviation+Log.xlsx"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Deviation Log Regulatory Template
                        </a>
                      </li>
                      <br></br>
                    </ul>

                    <br></br>

                    <h4 className={classes.heading}>References</h4>

                    <ul className={classes.list}>
                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO_Oxy_Table.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Oxygenation Table
                        </a>
                      </li>
                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/dinorecruitmentposter_template.docx"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Recruitment Poster
                        </a>
                      </li>
                      <br></br>
                      <li>
                        <a
                          href="https://www.youtube.com/playlist?list=PLyf0KvC3Jpe4XjcPuVNGQAZ1O5kx3Strj"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Link to playlist of all study training
                        </a>
                      </li>
                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+Accounts+List.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          DiNO Accounts List
                        </a>
                      </li>
                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+ScreeningDB+Template.xlsx"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Screening Log
                        </a>
                      </li>
                      <br></br>

                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+Regulatory+Checklist.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Regulatory Checklist
                        </a>
                      </li>
                      <br></br>
                    </ul>

                    <br></br>

                    <h4 className={classes.heading}>MOO</h4>

                    <ul className={classes.list}>
                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+MOO.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          MOO
                        </a>
                      </li>
                    </ul>

                    <br></br>

                    <h4 className={classes.heading}>Protocol</h4>

                    <ul className={classes.list}>
                      <li>
                        <a
                          href="https://prevent-prod-storage.s3.us-west-2.amazonaws.com/dino-docs/DiNO+Protocol.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          Protocol
                        </a>
                      </li>
                    </ul>

                    <br></br>

                    <h4 className={classes.heading}>FAQ</h4>

                    <ul className={classes.list}>
                      <li>
                        <a
                          href=" https://onedrive.live.com/edit?id=274601F23810F894!sfa2aa0ecfa2b45909e7c56d879eb4d9a&resid=274601F23810F894!sfa2aa0ecfa2b45909e7c56d879eb4d9a&cid=274601f23810f894&ithint=file%2Cxlsx&redeem=aHR0cHM6Ly8xZHJ2Lm1zL3gvYy8yNzQ2MDFmMjM4MTBmODk0L0VleWdLdm9yLXBCRm5ueFcySG5yVFpvQkhhUlFZWVVMTENfeGY4YmVSNlVzb1E_d2RMT1I9Y0E5QTY3QjE0LTkxRjktNDM0RC1BRjE4LTk0MDRFNTVBNTdDNSZPUj1PdXRsb29r&migratedtospo=true&wdLOR=cA9A67B14-91F9-434D-AF18-9404E55A57C5&wdo=2"
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          FAQ
                        </a>
                      </li>
                    </ul>

                    <br></br>
                  </div>
                )}

                {access < minimumAccessNeeded && (
                  <h4 className={classes.heading} style={{ color: "red" }}>
                    You must have at least Research Coordinator login access to
                    view and download Documents.
                  </h4>
                )}

                <h4 className={classes.heading}>Feedback/Questions </h4>

                <ul className={classes.list}>
                  Contact us at...<br></br>
                  <br></br>
                  <a
                    href="mailto:dinostudy@chla.usc.edu"
                    //target="_blank"
                    // rel="noopener noreferrer"
                  >
                    dinostudy@chla.usc.edu
                  </a>
                </ul>

                <br></br>
              </div>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

Dino.propTypes = {
  userInfo: PropTypes.array,
};

